import { useState } from "react";
import { Link } from "react-router";
import { CheckOutlined, PlusOutlined, CloseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import "../HomePage/HomePage.css"; // shared landing primitives (badge, btn, cta, tokens)
import "./PricingPage.css";

const TIERS = [
  {
    name: "Starter",
    audience: "Solo founders & pre-seed builders",
    price: "$25",
    period: "/month",
    actions: [{ label: "Start free", to: "/login", variant: "dark" }],
    features: [
      "Industry-tailored starter model",
      "Core projections & runway",
      "Basic scenarios",
      "Limited KPIs",
      "Read-only exports",
    ],
  },
  {
    name: "Founder",
    audience: "VC-backed startups · Seed → Series A",
    popular: true,
    actions: [
      { label: "Contact sales", to: "/contact-sales", variant: "dark" },
      { label: "Request a demo", to: "/contact-sales", variant: "gold" },
    ],
    features: [
      "Full investor-ready financials (60+ items)",
      "Dynamic scenarios",
      "Valuation & share pricing",
      "Cap table & dilution",
      "25+ KPIs",
      "Investor reports",
    ],
  },
  {
    name: "Growth",
    audience: "Scaling startups · Series A → B",
    actions: [
      { label: "Contact sales", to: "/contact-sales", variant: "dark" },
      { label: "Request a demo", to: "/contact-sales", variant: "gold" },
    ],
    features: [
      "Everything in Founder",
      "Advanced sensitivity analysis",
      "Multi-round fundraising",
      "Board-ready outputs",
      "Product & unit economics",
    ],
  },
];

const TEAMS = [
  {
    name: "Advisor",
    accent: "teal",
    audience: "Fractional CFOs & consultants",
    points: [
      "Multi-client management",
      "Standardized startup models",
      "Per-client scenario & reporting",
      "Volume pricing",
    ],
  },
  {
    name: "Portfolio",
    accent: "coral",
    audience: "Venture studios, accelerators, incubators",
    points: [
      "Portfolio-wide access",
      "Standardized frameworks",
      "Consistent investor outputs",
      "Founder onboarding support",
    ],
  },
  {
    name: "Investor",
    accent: "teal",
    audience: "VC & investment teams",
    points: [
      "Deal evaluation & stress-testing",
      "Valuation & dilution scenarios",
      "Standardized assumptions",
      "Internal IC use",
    ],
  },
  {
    name: "Innovation",
    accent: "coral",
    audience: "Corporate innovation & new venture teams",
    points: [
      "New-venture financial models",
      "Scenario planning",
      "Exec & board-ready outputs",
      "Multi-user access",
    ],
  },
];

const FAQS = [
  {
    q: "Can I switch plans later?",
    a: "Yes — upgrade or downgrade at any time. Billing is prorated automatically.",
  },
  {
    q: "Do you offer annual billing?",
    a: "Yes — annual billing is available with a discount. Talk to sales to set it up.",
  },
  {
    q: 'What counts as a "client" on Advisor?',
    a: "Each distinct company you model counts as one client. Manage as many as you need under a single workspace.",
  },
  {
    q: "Is my data secure?",
    a: "Yes — your data is encrypted in transit and at rest, and is never shared or sold.",
  },
  {
    q: "Do you have a free trial?",
    a: "Yes — the Starter plan begins free, with no credit card required.",
  },
  {
    q: "Can PreNumbers replace my fractional CFO?",
    a: "PreNumbers produces investor-grade financials in minutes, augmenting or replacing much of the modeling work a fractional CFO does.",
  },
];

function FaqItem({ q, a, open, onToggle }) {
  return (
    <div className={`pn-faq-item ${open ? "open" : ""}`}>
      <button className="pn-faq-q" onClick={onToggle}>
        <span>{q}</span>
        {open ? <CloseOutlined /> : <PlusOutlined />}
      </button>
      {open && <p className="pn-faq-a">{a}</p>}
    </div>
  );
}

const PricingPage = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="home pn-landing pn-pricing pn-bg-textured">
      {/* ===== HERO ===== */}
      <section className="pn-pr-hero">
        <div className="pn-container pn-pr-hero-inner">
          <span className="pn-badge">
            <i className="pn-badge-dot" /> Pricing
          </span>
          <h1 className="pn-pr-title">
            Plans that scale with your <span className="pn-gold">ambition.</span>
          </h1>
          <p className="pn-pr-sub">
            From solo founder to multi-client CFO to portfolio-wide investor operations. Pay monthly,
            cancel anytime.
          </p>
          <div className="pn-pr-hero-cta">
            <Link to="/contact-sales" className="pn-btn pn-btn-gold">
              Request a demo <ArrowRightOutlined />
            </Link>
            <Link to="/contact-sales" className="pn-btn pn-btn-dark">
              Talk to sales
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TIERS ===== */}
      <section className="pn-pr-tiers">
        <div className="pn-container pn-tier-grid">
          {TIERS.map((t) => (
            <div className={`pn-tier ${t.popular ? "popular" : ""}`} key={t.name}>
              {t.popular && <span className="pn-tier-pop">Most popular</span>}
              <h3 className="pn-tier-name">{t.name}</h3>
              <p className="pn-tier-aud">{t.audience}</p>
              {t.price && (
                <p className="pn-tier-price">
                  {t.price}
                  <span className="pn-tier-period">{t.period}</span>
                </p>
              )}
              <div className="pn-tier-actions">
                {t.actions.map((a) => (
                  <Link
                    key={a.label}
                    to={a.to}
                    className={`pn-btn ${a.variant === "gold" ? "pn-btn-gold" : "pn-btn-dark"}`}
                  >
                    {a.label}
                  </Link>
                ))}
              </div>
              <ul className="pn-tier-features">
                {t.features.map((f) => (
                  <li key={f}>
                    <CheckOutlined /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TEAMS ===== */}
      <section className="pn-pr-teams">
        <div className="pn-container">
          <div className="pn-teams-head">
            <div>
              <span className="pn-badge">
                <i className="pn-badge-dot" /> Teams &amp; organizations
              </span>
              <h2 className="pn-teams-title">For advisors, studios, and institutional teams</h2>
            </div>
            <Link to="/contact-sales" className="pn-teams-quote">
              Get a custom quote <ArrowRightOutlined />
            </Link>
          </div>
          <div className="pn-team-grid">
            {TEAMS.map((tm) => (
              <div className={`pn-team-card ${tm.accent}`} key={tm.name}>
                <div className="pn-team-head">
                  <div>
                    <h3 className="pn-team-name">{tm.name}</h3>
                    <span className="pn-team-aud">{tm.audience}</span>
                  </div>
                  <Link to="/contact-sales" className="pn-btn pn-btn-dark pn-team-btn">
                    Contact sales
                  </Link>
                </div>
                <ul className="pn-team-points">
                  {tm.points.map((p) => (
                    <li key={p}>
                      <i className="pn-team-dot" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="pn-pr-faq">
        <div className="pn-container">
          <span className="pn-badge pn-badge-center">
            <i className="pn-badge-dot" /> FAQ
          </span>
          <h2 className="pn-section-title">Questions, answered.</h2>
          <div className="pn-faq-list">
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="pn-pr-cta">
        <div className="pn-container">
          <div className="pn-cta-card">
            <div className="pn-cta-content">
              <span className="pn-badge">
                <i className="pn-badge-dot" /> Get started
              </span>
              <h2 className="pn-cta-title">
                See investor-grade FP&amp;A in <span className="pn-gold">minutes.</span>
              </h2>
              <p>
                A PreNumbers specialist walks you through an example of industry tailored model for
                your venture — projections, valuation, cap table, scenarios — live.
              </p>
            </div>
            <div className="pn-cta-actions">
              <Link to="/contact-sales" className="pn-btn pn-btn-gold">
                Request a demo <ArrowRightOutlined />
              </Link>
              <Link to="/pricing" className="pn-btn pn-btn-ghost">
                See pricing
              </Link>
              <span className="pn-cta-note">No credit card · 20-min call · investor-grade output</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
